import { useMemo } from 'react';
import { useListingRestrictions } from 'hooks/use-listing-restrictions';
import {
  PriceHistoryRecord,
  PriceHistoryTimeline,
} from 'domain/listing/value-objects';
import { CountryCodeList } from 'types/countries';
import { hasFeatureFlag, FEATURE_FLAGS } from '../../types';
import type { AddressPageCommonData } from '../../shared';

/**
 * Hook to calculate visibility flags for listing sections (tabs).
 * 
 * Centralizes the logic for determining which sections should be visible
 * based on feature flags, data availability, and restrictions.
 * 
 * Used by:
 * - DesktopLayout (for sticky nav tabs)
 * - ContentSectionWithTabs (for content sections)
 */
export function useListingSectionVisibility(data: AddressPageCommonData) {
  const { address, listing, appraisal, flags, history } = data;

  // Determine country for restrictions
  const listingCountryCode = address.countryCode.toUpperCase() === CountryCodeList.CANADA 
    ? CountryCodeList.CANADA 
    : CountryCodeList.UNITED_STATES;
  
  const { canViewPropertyAppraisal } = useListingRestrictions(listingCountryCode);
  
  // Calculate visibility conditions
  const hasAddressCoordinates = listing.position?.coordinates?.length === 2;
  const streetAddressAllowed = listing.displayAddress !== false;
  const isCommuteTabVisible = hasAddressCoordinates && streetAddressAllowed;
  const shouldShowMap = streetAddressAllowed;
  const isPropertyAppraisalVisible = 
    hasFeatureFlag(flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED) && 
    canViewPropertyAppraisal && 
    appraisal?.appraisalResult?.isAvailable;
  const hasSimilarListings = 
    hasFeatureFlag(flags, FEATURE_FLAGS.SIMILAR_LISTINGS_ENABLED) && 
    hasAddressCoordinates &&
    streetAddressAllowed;
  // Price history tab should only show when the timeline has a real historical row beyond the current listing.
  const hasPriceHistory = PriceHistoryTimeline
    .from(history.map(record => PriceHistoryRecord.fromListing(record)))
    .hasHistory(listing.id);
  
  // Return visibility flags
  return useMemo(() => ({
    shouldShowPriceHistory:
      hasFeatureFlag(flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED) &&
      !listing.hidePriceHistory &&
      hasPriceHistory,
    shouldShowAppraisal: isPropertyAppraisalVisible,
    shouldShowSimilarListings: hasSimilarListings,
    shouldShowCommute: isCommuteTabVisible,
    shouldShowMap,
  }), [flags, hasPriceHistory, isPropertyAppraisalVisible, hasSimilarListings, isCommuteTabVisible, listing.hidePriceHistory, shouldShowMap]);
}
